import React, { useState } from "react";
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useLocation, useNavigate } from "react-router-dom";
import { UserList } from "../../../../../models/User/User";
import { SortType } from "../../../../../models/SortType";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../../../models/Employees/EmployeeRoles";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import { getModernPageColors } from "../../../../../styles/modernListingPageSx";

interface UserTableProp {
  maxHeight: number | string;
  users: UserList[];
  limit: number;
  page_no: number;
  total_pages: number;
  total_count: number;
  sortBy: string;
  onDelete: (userId: number) => void;
  onActivate?: (userId: number) => void;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void;
  onRowsPerPageChange?: (limit: number) => void;
  sortType: SortType;
  onSortChange: (column: string, sortType: SortType) => void;
}

interface ColumnConfig {
  id: string;
  label: string;
  sortable?: boolean;
  minWidth?: number | string;
  align?: "left" | "center" | "right";
}

const UserTable: React.FC<UserTableProp> = ({
  maxHeight,
  users,
  page_no,
  limit,
  total_pages,
  total_count,
  sortBy,
  onPageChange,
  onDelete,
  onActivate,
  sortType,
  onSortChange,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const openUser = (userId: number) => {
    navigate(`/users/add-edit-user?user-id=${userId}`, {
      state: { listingSearch: location.search },
    });
  };
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const c = theme.palette.customColors;
  const { userStore } = useStores();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [currentUser, setCurrentUser] = useState<UserList | null>(null);

  const isOpen = Boolean(anchorEl);

  const canManageUser =
    userStore.hasPermission(EmployeeRoles.CREATE_USER) ||
    userStore.hasPermission(EmployeeRoles.EDIT_USER);

  const rowClickable = (): boolean => {
    return (
      userStore.hasPermission(EmployeeRoles.VIEW_USER) ||
      userStore.hasPermission(EmployeeRoles.EDIT_USER) ||
      userStore.hasPermission(EmployeeRoles.CREATE_USER)
    );
  };

  const isInactiveUser = (user?: UserList | null): boolean =>
    user?.active === 0 || user?.active === false;

  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    user: UserList,
  ) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setCurrentUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setCurrentUser(null);
  };

  const handleEdit = () => {
    if (currentUser?.id) {
      openUser(Number(currentUser.id));
    }
    handleMenuClose();
  };

  const handleDelete = () => {
    if (currentUser?.id) {
      onDelete(Number(currentUser.id));
    }
    handleMenuClose();
  };

  const handleActivate = () => {
    if (currentUser?.id && onActivate) {
      onActivate(Number(currentUser.id));
    }
    handleMenuClose();
  };

  const handleSort = (colId: string) => {
    if (sortBy === colId) {
      onSortChange(
        colId,
        sortType === SortType.ASC ? SortType.DESC : SortType.ASC,
      );
    } else {
      onSortChange(colId, SortType.DESC);
    }
  };

  const renderSortIcon = (colId: string) => {
    if (sortBy === colId) {
      return sortType === SortType.ASC ? "↑" : "↓";
    }
    return "↑↓";
  };

  const formatDateTime = (dateVal?: Date | string | null) => {
    if (!dateVal) return "-";
    try {
      const d = new Date(dateVal);
      if (isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "-";
    }
  };

  const renderBoolean = (val: any) => {
    const isTrue = val === true || val === 1 || val === "1" || val === "true";
    return (
      <CustomTypography
        variant="body2"
        fontWeight={500}
        color={isTrue ? colors.success : colors.error}
      >
        {isTrue ? "True" : "False"}
      </CustomTypography>
    );
  };

  const bodyCellSx = {
    py: 2,
    px: 2,
    borderBottom: `1px solid ${c.lightGreyColor}`,
    whiteSpace: "nowrap" as const,
  };

  const stickyShadow = `-4px 0 8px -2px ${c.shadowLightBorder}`;

  const columns: ColumnConfig[] = [
    { id: "id", label: "User Id", sortable: true, minWidth: 90 },
    {
      id: "company_name",
      label: "Company Name",
      sortable: true,
      minWidth: 160,
    },
    { id: "user_name", label: "User Name", sortable: false, minWidth: 140 },
    {
      id: "customer_id",
      label: "Customer ID",
      sortable: true,
      minWidth: 130,
    },
    {
      id: "customer_name",
      label: "Customer Name",
      sortable: false,
      minWidth: 160,
    },
    {
      id: "added_by_name",
      label: "Last Edited By",
      sortable: true,
      minWidth: 150,
    },
    { id: "email", label: "Email", sortable: false, minWidth: 220 },
    {
      id: "created_date",
      label: "Created On",
      sortable: true,
      minWidth: 170,
    },
    {
      id: "modified_date",
      label: "Last Modified",
      sortable: true,
      minWidth: 170,
    },
    { id: "active", label: "Active", sortable: false, minWidth: 90 },
    {
      id: "actions",
      label: "Actions",
      sortable: false,
      minWidth: 80,
      align: "center",
    },
  ];

  const fromRange = total_count === 0 ? 0 : (page_no - 1) * limit + 1;
  const toRange = Math.min(page_no * limit, total_count);

  return (
    <Box sx={{ width: "100%", overflow: "hidden" }}>
      <TableContainer
        sx={{
          maxHeight,
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <Table stickyHeader sx={{ minWidth: 1450 }}>
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const isActions = col.id === "actions";
                return (
                  <TableCell
                    key={col.id}
                    align={col.align || "left"}
                    onClick={
                      col.sortable ? () => handleSort(col.id) : undefined
                    }
                    sx={{
                      py: 1.75,
                      px: 2,
                      minWidth: col.minWidth,
                      backgroundColor: colors.surface,
                      borderBottom: `1px solid ${colors.border}`,
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: colors.label,
                      whiteSpace: "nowrap",
                      cursor: col.sortable ? "pointer" : "default",
                      userSelect: col.sortable ? "none" : "auto",
                      ...(isActions
                        ? {
                            position: "sticky",
                            right: 0,
                            top: 0,
                            zIndex: 13,
                            boxShadow: stickyShadow,
                            borderLeft: `1px solid ${c.lightGreyColor}`,
                          }
                        : {}),
                      "&:hover": col.sortable
                        ? { color: colors.accentPurple }
                        : undefined,
                    }}
                  >
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <span>{col.label}</span>
                      {col.sortable && (
                        <CustomTypography
                          variant="caption"
                          fontWeight={500}
                          color={
                            sortBy === col.id ? colors.title : c.mediumGray
                          }
                          style={{ marginLeft: 4 }}
                        >
                          {renderSortIcon(col.id)}
                        </CustomTypography>
                      )}
                    </Box>
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user: UserList) => {
              const isRowClickable = rowClickable() && !isInactiveUser(user);

              return (
                <TableRow
                  key={user.id}
                  hover
                  onClick={() => {
                    if (isRowClickable) {
                      openUser(Number(user.id));
                    }
                  }}
                  sx={{
                    cursor: isRowClickable ? "pointer" : "default",
                    backgroundColor: colors.surface,
                    "&:hover": {
                      backgroundColor: `${c.lightGrayBackground} !important`,
                      "& .sticky-action-cell": {
                        backgroundColor: `${c.lightGrayBackground} !important`,
                      },
                    },
                    borderBottom: `1px solid ${c.lightGreyColor}`,
                  }}
                >
                  <TableCell sx={bodyCellSx}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      color={colors.title}
                    >
                      {user.id}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography
                      variant="body2"
                      fontWeight={600}
                      color={colors.title}
                    >
                      {user.company_name || "-"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {`${user?.first_name || ""} ${user?.last_name || ""}`.trim() ||
                        user?.name ||
                        "-"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {user.customer_id || "-"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {user.customer_name || "-"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {user.added_by_name || "-"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {user.email || "-"}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {formatDateTime(user.created_date)}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    <CustomTypography variant="body2" color={c.navyText}>
                      {formatDateTime(user.modified_date)}
                    </CustomTypography>
                  </TableCell>

                  <TableCell sx={bodyCellSx}>
                    {renderBoolean(user.active)}
                  </TableCell>

                  <TableCell
                    className="sticky-action-cell"
                    align="center"
                    onClick={(e) => e.stopPropagation()}
                    sx={{
                      py: 1.5,
                      px: 1,
                      borderBottom: `1px solid ${c.lightGreyColor}`,
                      position: "sticky",
                      right: 0,
                      zIndex: 1,
                      backgroundColor: colors.surface,
                      boxShadow: stickyShadow,
                      borderLeft: `1px solid ${c.lightGreyColor}`,
                    }}
                  >
                    {(!isInactiveUser(user) ||
                      (canManageUser && onActivate)) && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuClick(e, user)}
                        sx={{
                          color: colors.subtitle,
                          p: 0.5,
                          "&:hover": {
                            backgroundColor: c.lightGreyColor,
                            color: colors.title,
                          },
                        }}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={isOpen}
        onClose={handleMenuClose}
        elevation={0}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        slotProps={{
          paper: {
            sx: {
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
              boxShadow: `0 4px 16px ${c.shadowMedium}`,
              minWidth: 130,
              py: 0.5,
              backgroundColor: colors.surface,
            },
          },
        }}
      >
        {!isInactiveUser(currentUser) && (
          <MenuItem
            onClick={handleEdit}
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              color: c.navyText,
              py: 1,
              px: 2,
              "&:hover": {
                backgroundColor: colors.headerBg,
                color: colors.accentPurple,
              },
            }}
          >
            View/Edit
          </MenuItem>
        )}
        {canManageUser && !isInactiveUser(currentUser) && (
          <MenuItem
            onClick={handleDelete}
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              color: colors.error,
              py: 1,
              px: 2,
              "&:hover": {
                backgroundColor: `${colors.error}14`,
              },
            }}
          >
            Delete
          </MenuItem>
        )}
        {canManageUser && isInactiveUser(currentUser) && onActivate && (
          <MenuItem
            onClick={handleActivate}
            sx={{
              fontSize: "0.8rem",
              fontWeight: 500,
              color: colors.success,
              py: 1,
              px: 2,
              "&:hover": {
                backgroundColor: c.successGreenLightBackground,
              },
            }}
          >
            Activate User
          </MenuItem>
        )}
      </Menu>

      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
          px: 2.5,
          py: 2,
          borderTop: `1px solid ${colors.border}`,
          backgroundColor: colors.surface,
        }}
      >
        <CustomTypography
          variant="caption"
          color={c.mediumGray}
          style={{ userSelect: "none" }}
        >
          ← Scroll horizontally →
        </CustomTypography>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 2, sm: 3 },
            ml: { xs: 0, sm: "auto" },
            flexWrap: "wrap",
          }}
        >
          <CustomTypography
            variant="caption"
            color={c.mediumGray}
            fontWeight={600}
            style={{ letterSpacing: "0.05em", textTransform: "uppercase" }}
          >
            SHOWING {users.length} OF {total_count} ROWS
          </CustomTypography>

          <CustomTypography
            variant="caption"
            color={c.navyText}
            fontWeight={500}
          >
            {fromRange} - {toRange} of {total_count}
          </CustomTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <IconButton
              size="small"
              disabled={page_no <= 1}
              onClick={(e) => onPageChange(e, page_no - 1)}
              sx={{
                color: c.mutedSlateText,
                p: 0.5,
                "&.Mui-disabled": { color: c.switchTrackGray },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              disabled={
                page_no >= total_pages ||
                (limit > 0 && page_no * limit >= total_count)
              }
              onClick={(e) => onPageChange(e, page_no + 1)}
              sx={{
                color: c.mutedSlateText,
                p: 0.5,
                "&.Mui-disabled": { color: c.switchTrackGray },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default UserTable;
